import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import {
    count,
    read,
    listRoles,
    getUser,
    userListForClient,
    AssigneeTaskUserListProject,
} from './users.service'

const initialState = {
    search: '',
    status: 'loaded',
    getUserStatus: 'loaded',
    roleListStatus: 'loaded',
    users: [],
    userRole: [],
    usersCount: 0,
    isNext: false,
    pageLimit: 10,
    currentPage: 1,
    formButtonType: 'saveButtonLoading',
    skipPage: 0,
    currentUser: 0,
    formModal: false,
    // Client modal user list state
    clientModalUserPage: 1,
    clientModalUserSearch: '',
    clientModalUsersListData: [],
    clientModalUserIsNext: false,
    modalVariations: {
        open: false,
        formName: 'userAddForm',
        saveButtonLoading: false,
    },
    viewModal: false,
    filterView: false,
    filterDropDown: {
        role: '',
        department: '',
        designation: '',
        isAssigned: true,
    },
    selectedUser: undefined,
    projectTaskAssigneeUser: [],
    projectTaskAssigneeUserListLoading: false,
    projectTaskAssigneeUserIsNext: false,

}

export const totalUsers = createAsyncThunk(
    'usersSlice/count',
    async (payload = {}) => {
        const response = await count(payload)
        return response
    }
)

export const usersList = createAsyncThunk(
    'usersSlice/read',
    async (payload = {}) => {
        const response = await read(payload)
        return response
    }
)

export const roleList = createAsyncThunk(
    'usersSlice/listRoles',
    async (payload = {}) => {
        const response = await listRoles(payload)
        return response
    }
)

export const userGet = createAsyncThunk(
    'usersSlice/getUser',
    async (payload = {}) => {
        const response = await getUser(payload)
        return response
    }
)

export const usersListForClientModal = createAsyncThunk(
    'usersSlice/userListForClient',
    async (payload = {}) => {
        const response = await userListForClient(payload)
        return response
    }
)


export const projectTaskListAssigneeUser = createAsyncThunk(
    'usersSlice/projectTaskListAssigneeUser',
    async (payload = {}) => {
        const response = await AssigneeTaskUserListProject(payload)
        return response
    }
)

export const usersSlice = createSlice({
    name: 'usersSlice',
    initialState,
    reducers: {
        filterDropdown: (state, action) => {
            const dropDownValue = {
                ...state.filterDropDown,
                ...action.payload,
            }
            // Object.keys(dropDownValue).forEach((key) => {
            //     if (dropDownValue[key] === '') {
            //         delete dropDownValue[key]
            //     }
            // })
            state.filterDropDown = dropDownValue
        },
        clearFilterDropDown: (state) => {
            state.filterDropDown = initialState.filterDropDown
        },

        checkButtonType: (state, action) => {
            state.formButtonType = action.payload
        },
        searchUsers: (state, action) => {
            state.search = action.payload
        },
        clearUsers: (state) => {
            state.users = []
            state.search = ''
        },
        changePage: (state, action) => {
            state.currentPage = action.payload.currentPage
            state.skipPage = action.payload.skip
            state.pageLimit = action.payload.pageLimit
        },
        // Client modal user list actions
        resetClientModalUsers: (state) => {
            state.clientModalUsersListData = []
            state.clientModalUserPage = 1
            state.clientModalUserSearch = ''
            state.clientModalUserIsNext = false
        },
        setClientModalUserSearch: (state, action) => {
            state.clientModalUserSearch = action.payload
            state.clientModalUserPage = 1
            state.clientModalUsersListData = []
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(usersList.pending, (state) => {
                state.status = 'loading'
            })
            .addCase(usersList.fulfilled, (state, action) => {
                state.status = 'loaded'
                if (action.payload.skip > 0) {
                    state.users = [...state.users, ...action.payload.data]
                } else {
                    state.users = action.payload.data
                }
                state.isNext = action.payload.isNext
                state.currentUser = action.payload.currentCount
            })
            .addCase(userGet.pending, (state) => {
                state.getUserStatus = 'loading'
            })
            .addCase(userGet.fulfilled, (state, action) => {
                state.getUserStatus = 'loaded'
                state.selectedUser = action.payload.data.data
            })
            .addCase(usersListForClientModal.pending, (state) => {
                state.getUserStatus = 'loading'
            })
            .addCase(usersListForClientModal.fulfilled, (state, action) => {
                state.getUserStatus = 'loaded'
                const { skip = 0, search = '' } = action.meta.arg || {}

                // Check if this is a new search (skip === 0) or loading more
                if (skip === 0) {
                    // New search or initial load - replace data
                    state.clientModalUsersListData = action.payload.data
                    state.clientModalUserPage = 1
                    state.clientModalUserSearch = search
                } else {
                    // Loading more - append data
                    state.clientModalUsersListData = [
                        ...state.clientModalUsersListData,
                        ...action.payload.data,
                    ]
                    state.clientModalUserPage = state.clientModalUserPage + 1
                }

                state.clientModalUserIsNext = action.payload.isNext
            })
            .addCase(projectTaskListAssigneeUser.pending, (state, action) => {
                state.projectTaskAssigneeUserListLoading = 'loading'
            })
            .addCase(projectTaskListAssigneeUser.fulfilled, (state, action) => {
                state.projectTaskAssigneeUserListLoading = 'loaded'
                const skip = action.meta?.arg?.skip || 0
                const nextData = action.payload?.data || []
                state.projectTaskAssigneeUser =
                    skip > 0
                        ? [
                              ...(state.projectTaskAssigneeUser || []),
                              ...nextData,
                          ]
                        : nextData
                state.projectTaskAssigneeUserIsNext = Boolean(
                    action.payload?.isNext
                )
            })
            .addCase(roleList.pending, (state, action) => {
                state.roleListStatus = 'loading'
            })
            .addCase(roleList.fulfilled, (state, action) => {
                state.roleListStatus = 'loaded'
                state.userRole = action.payload.data
            })
            .addCase(totalUsers.fulfilled, (state, action) => {
                state.usersCount = action.payload.data
            })
    },
})

export default usersSlice.reducer

/** Return the slice reference — never rebuild an object (avoids rerender warnings). */
export const usersState = (state) => state.usersSlice || initialState

export const {
    searchUsers,
    filterDropdown,
    clearFilterDropDown,
    clearUsers,
    changePage,
    checkButtonType,
    resetClientModalUsers,
    setClientModalUserSearch,
} = usersSlice.actions
